import { useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ClipboardList, Download, ChevronDown } from "lucide-react"
import type { RawContact } from "../types"
import type { FormFieldMeta } from "./VariablesTab"
import { computeVarBreakdown } from "../../hooks/useVariablesAnalytics"
import { VarBreakdownChart } from "../shared/VarBreakdownChart"
import { ExportDialog } from "../shared/ExportDialog"
import { buildFormGroups, formRespondents, responsesExport, summaryExport } from "../../utils/formsReport"

interface FormsTabProps {
  filteredContacts: RawContact[]
  formFieldMeta: Record<string, FormFieldMeta>
  formRecordUpdatedAt: Record<string, string>
}

type ExportMode = "responses" | "summary"

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "form"

export function FormsTab({ filteredContacts, formFieldMeta, formRecordUpdatedAt }: FormsTabProps) {
  const [exporting, setExporting] = useState<{ formId: string; mode: ExportMode } | null>(null)

  const forms = useMemo(() => {
    return buildFormGroups(formFieldMeta).map(group => {
      const respondents = formRespondents(group, filteredContacts)
      const charts = group.fields.map(f => ({
        field: f,
        // Respondents only, and no message fallback: form answers live on the contact
        breakdown: computeVarBreakdown(f.key, respondents, []),
      }))
      return { group, respondents, charts }
    })
  }, [formFieldMeta, filteredContacts])

  const withResponses = forms.filter(f => f.respondents.length > 0)
  const withoutResponses = forms.length - withResponses.length

  const active = exporting && forms.find(f => f.group.formId === exporting.formId)
  const exportData = active
    ? exporting!.mode === "responses"
      ? responsesExport(active.group, active.respondents, formRecordUpdatedAt)
      : summaryExport(active.group, active.respondents)
    : null

  if (withResponses.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 border border-dashed rounded-lg text-muted-foreground">
        <div className="text-center space-y-2">
          <ClipboardList className="h-6 w-6 mx-auto opacity-30" />
          <p className="text-sm">No Magic Form responses in this range</p>
          {forms.length > 0 && (
            <p className="text-xs">{forms.length} form{forms.length === 1 ? "" : "s"} set up, none answered yet</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {withResponses.map(({ group, respondents, charts }) => (
        <Card key={group.formId}>
          <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate">{group.title}</span>
              </CardTitle>
              <CardDescription>
                <Badge variant="secondary" className="font-normal">
                  {respondents.length} response{respondents.length === 1 ? "" : "s"}
                </Badge>
                <span className="ml-2">{group.fields.length} question{group.fields.length === 1 ? "" : "s"}</span>
              </CardDescription>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem onSelect={() => setExporting({ formId: group.formId, mode: "responses" })}>
                  <div>
                    <p className="text-sm font-medium">Responses</p>
                    <p className="text-xs text-muted-foreground">One row per response, one column per question</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setExporting({ formId: group.formId, mode: "summary" })}>
                  <div>
                    <p className="text-sm font-medium">Summary</p>
                    <p className="text-xs text-muted-foreground">Each answer with its count and percentage</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {charts.map(({ field, breakdown }) => (
                <Card key={field.key} className="min-w-0">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-sm font-medium truncate" title={field.meta.title}>
                      {field.meta.title || field.key.slice("form.".length)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 min-w-0">
                    <VarBreakdownChart
                      breakdown={breakdown}
                      labelFor={v => field.meta.optionLabels?.[v] ?? v}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {withoutResponses > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {withoutResponses} other form{withoutResponses === 1 ? " has" : "s have"} no responses in this range
        </p>
      )}

      {active && exportData && (
        <ExportDialog
          // Remount per form and mode: the dialog picks its column selection once
          key={`${active.group.formId}:${exporting!.mode}`}
          open
          onOpenChange={open => { if (!open) setExporting(null) }}
          title={`Export ${active.group.title}`}
          description={exporting!.mode === "responses"
            ? `${exportData.data.length} responses as CSV`
            : `Answer counts for ${active.group.fields.length} questions as CSV`}
          columns={exportData.columns}
          data={exportData.data}
          filename={`${slug(active.group.title)}_${exporting!.mode}`}
        />
      )}
    </div>
  )
}
